import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Phone } from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { SITE, CITIES, SERVICES, PHONE_DISPLAY, PHONE_TEL } from "@/seoData";
import { FEATURED_AREAS } from "@/siteContent";

const MAP_SRC = "https://www.google.com/maps?q=San%20Diego%20County%2C%20CA&z=9&output=embed";

export default function ServiceAreas() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const canonical = `${SITE}/service-areas`;
  const title = "Service Areas | Junk Removal Across San Diego County | CleanOut247";
  const description = "CleanOut247 provides same-day junk removal across San Diego County — San Diego, Rancho Bernardo, 4S Ranch, Rancho Santa Fe, Del Mar, Solana Beach, Encinitas, Carlsbad, Oceanside, Escondido, Poway, La Jolla and more.";

  const jsonLd = [{
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
    "@id": "https://cleanout247.com/#business",
    name: "CleanOut247",
    url: SITE,
    telephone: "+1-619-999-3565",
    areaServed: CITIES.map((c) => ({ "@type": "City", name: `${c.name}, CA` })).concat({ "@type": "AdministrativeArea", name: "San Diego County, CA" }),
  }];

  return (
    <div className="bg-white text-[#0A0A0A] overflow-x-hidden" data-testid="service-areas-page">
      <Seo title={title} description={description} canonical={canonical} jsonLd={jsonLd} />
      <Header />

      <section className="pt-28 sm:pt-40 lg:pt-48 pb-14 px-5 lg:px-8 bg-[#0B1E45] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[#5DB82F] font-display font-bold uppercase tracking-widest text-xs mb-4">
            <span className="h-px w-8 bg-[#5DB82F]" /> Where We Work
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl">Serving all of San Diego County</h1>
          <p className="text-white/70 mt-5 text-lg leading-relaxed max-w-3xl">
            Based in the Rancho Bernardo / 4S Ranch area, CleanOut247 offers same-day junk removal
            across San Diego County — including San Diego, San Marcos, Carlsbad, Oceanside, Encinitas,
            Vista, Escondido, Poway and surrounding communities.
          </p>
        </div>
      </section>

      <section className="px-5 lg:px-8 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <div className="rounded-sm overflow-hidden border border-[#E4E4E7] shadow-sm order-2 lg:order-1">
            <iframe
              title="CleanOut247 service area map"
              data-testid="service-areas-map"
              src={MAP_SRC}
              width="100%"
              height="480"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>

          <div className="order-1 lg:order-2">
            <h2 className="font-display font-black tracking-tighter text-3xl text-[#15306E] mb-2">North San Diego & North County</h2>
            <p className="text-[#52525B] mb-4">These North County areas get our fastest pickup.</p>
            <div className="flex flex-wrap gap-2 mb-10" data-testid="service-areas-featured">
              {FEATURED_AREAS.map((a) => (
                <Link key={a.slug} to={`/junk-removal/${a.slug}`} data-testid={`featured-area-${a.slug}`} className="bg-[#F4FAF0] border border-[#E4E4E7] hover:border-[#46A01E] px-3 py-2 rounded-sm text-sm text-[#15306E] font-semibold transition-colors">
                  {a.name}{a.zip ? ` (${a.zip})` : ""}
                </Link>
              ))}
            </div>
            <h2 className="font-display font-black tracking-tighter text-3xl text-[#15306E] mb-5">All areas we cover</h2>
            <div className="grid sm:grid-cols-2 gap-2.5" data-testid="service-areas-list">
              {CITIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/junk-removal/${c.slug}`}
                  data-testid={`area-link-${c.slug}`}
                  className="flex items-center gap-2 bg-[#F4FAF0] border border-[#E4E4E7] hover:border-[#46A01E] hover:bg-[#EAF4E3] px-4 py-3 rounded-sm text-sm text-[#15306E] font-semibold transition-colors"
                >
                  <MapPin size={15} weight="fill" className="text-[#46A01E] shrink-0" /> Junk Removal {c.name}
                </Link>
              ))}
            </div>
            <p className="text-sm text-[#71717A] mt-5">
              Don't see your neighborhood? We likely still cover it —{" "}
              <a href={`tel:${PHONE_TEL}`} className="text-[#46A01E] font-semibold hover:underline">call or text {PHONE_DISPLAY}</a>.
            </p>
          </div>
        </div>

        {/* Services quick links */}
        <div className="max-w-7xl mx-auto mt-14">
          <h2 className="font-display font-black tracking-tighter text-3xl text-[#15306E] mb-5">Popular services</h2>
          <div className="flex flex-wrap gap-2.5">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="bg-white border border-[#E4E4E7] hover:border-[#46A01E] px-4 py-2.5 rounded-sm text-sm text-[#15306E] font-semibold transition-colors">
                {s.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 lg:px-8 py-14 text-center bg-[#F4F4F5]">
        <h2 className="font-display font-black tracking-tighter text-3xl text-[#15306E]">Ready when you are.</h2>
        <p className="text-[#52525B] mt-3">Same-day pickups throughout the county.</p>
        <div className="flex flex-wrap justify-center gap-3 mt-6">
          <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide text-sm px-6 py-4 rounded-sm transition-colors">
            <Phone size={18} weight="fill" /> Call/Text {PHONE_DISPLAY}
          </a>
          <Link to="/#estimate" className="flex items-center gap-2 border border-[#E4E4E7] hover:border-[#46A01E] text-[#15306E] font-display font-extrabold uppercase tracking-wide text-sm px-6 py-4 rounded-sm transition-colors">
            Get Instant Estimate <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
